// (c) chaehongee. Confidential and proprietary.

import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from './hooks'
import { uploadNode } from '../../services/node'
import { addNode } from '../slices/sessionSlice'
import { setNodes as setSelectedNodes } from '../slices/selectionSlice'
import { selectActiveSession, selectActiveSessionId } from '../selectors/sessionSelectors'

function isEditableTarget(target: EventTarget | null): boolean {
    const el = target as Element | null
    if (!el) return false
    const tag = (el as HTMLElement).tagName?.toLowerCase()
    const contentEditable = (el as HTMLElement).isContentEditable
    return (
        contentEditable ||
        tag === 'input' ||
        tag === 'textarea' ||
        (el.closest && !!el.closest('[contenteditable="true"]'))
    )
}

function mimeToExt(mime: string): string {
    if (mime.includes('png')) return '.png'
    if (mime.includes('jpeg') || mime.includes('jpg')) return '.jpg'
    if (mime.includes('webp')) return '.webp'
    if (mime.includes('gif')) return '.gif'
    return ''
}

export function useGlobalPasteUpload(): void {
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const session = useAppSelector(selectActiveSession)
    const dispatch = useAppDispatch()

    useEffect(() => {
        if (!session || !activeSessionId) return

        const onPaste = async (e: ClipboardEvent) => {
            if (!session) return
            if (isEditableTarget(e.target)) return

            const cd = e.clipboardData
            if (!cd) return

            const items = Array.from(cd.items as any) as DataTransferItem[]
            const imageItems = items.filter((it): it is DataTransferItem => {
                return it.kind === 'file' && typeof (it as any).type === 'string' && (it as any).type.startsWith('image/')
            })

            const uploadedIds: string[] = []

            for (const item of imageItems) {
                const fileFromClipboard = item.getAsFile()
                if (!fileFromClipboard) continue
                const blob = fileFromClipboard as File
                const name = `pasted-image${mimeToExt(blob.type)}`
                const file = blob instanceof File ? blob : new File([blob], name, { type: (blob as any).type })
                try {
                    const node = await uploadNode(file, session.id, 'image')
                    dispatch(addNode({ sessionId: activeSessionId, node }))
                    if ((node as any)?.id) uploadedIds.push((node as any).id)
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Paste image upload failed', err)
                }
            }

            if (uploadedIds.length > 0) {
                dispatch(setSelectedNodes({ sessionId: activeSessionId, nodeIds: uploadedIds.slice(-2) }))
                e.preventDefault()
                return
            }

            const text = cd.getData('text/plain') || ''
            if (text && /^https?:\/\//.test(text)) {
                try {
                    const resp = await fetch(text, { mode: 'cors' })
                    const contentType = resp.headers.get('content-type') || ''
                    if (resp.ok && contentType.startsWith('image/')) {
                        const blob = await resp.blob()
                        const file = new File([blob], `pasted-image${mimeToExt(contentType)}`, { type: contentType })
                        const node = await uploadNode(file, session.id, 'image')
                        dispatch(addNode({ sessionId: activeSessionId, node }))
                        if ((node as any)?.id) dispatch(setSelectedNodes({ sessionId: activeSessionId, nodeIds: [(node as any).id] }))
                        e.preventDefault()
                    }
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Fetching image from URL on clipboard failed', err)
                }
            }
        }

        window.addEventListener('paste', onPaste as any)
        return () => {
            window.removeEventListener('paste', onPaste as any)
        }
    }, [session, activeSessionId, dispatch])
}


